/**
 * The NURSE-facing projection of a request — T1033 M7.
 *
 * ⛔ THIS FILE IS THE MECHANISM, NOT A CONVENIENCE. Rule 1 (customers never see
 * a price) and OWASP **API3:2023 (Broken Object Property Level Authorization)**
 * both forbid *sending* a figure to the nurse's browser and hiding it there.
 * So the trimming happens HERE, on the server, before anything is serialised —
 * a client-side filter is explicitly not the mechanism.
 *
 * ⛔ PICK, NEVER OMIT. Built by naming the keys to KEEP, copying
 * `quote-helper/src/core/portal-ops-projection.ts`'s shape. A
 * `{...row}`-then-`delete` projection leaks every field added later by default;
 * this one drops them by default. That asymmetry is the entire design.
 *
 * ⚠️ THIS CLOSES A PRE-EXISTING LEAK, not a new one. `GET /api/requests`
 * returned EVERY key of `RequestListItem` while `MyRequests.tsx` rendered five.
 * Among the unrendered were exactly the ones the kiosk seam already refuses to
 * carry: `equipment` (Article 9 health data), `room`, `nextOfKinPhone`, `notes`.
 * ⚠️ THE COUNT WAS QUOTED HERE AS "**20**" AND WAS STALE BY THREE when T1717 M4
 * re-derived it (24 at M4's tip, 23 before it: `jobRef`, `kioskDestinationId`
 * and `returnTrip` all landed after the figure was written). It is deliberately
 * no longer stated — derive it from `mapListRow` in `core/requests-repo.ts`.
 *
 * ⭐ T1717 M4 (DD3/DD9) — `nextOfKinName` JOINS THAT SET, AND IT IS DROPPED HERE
 * EXACTLY AS `nextOfKinPhone` IS. It reaches the request row and the PORTAL's
 * `/ops` table and nowhere else; it is not in the keep-set below, so the PICK
 * drops it by construction rather than by anybody remembering to. ⛔ The pin is
 * not this sentence: the spec's `CANARY` fixture carries a
 * `CANARY-MUST-NOT-CROSS` value in that field and the value sweep reds if it
 * ever appears on the nurse's wire.
 *
 * ⛔ BUT DO NOT READ THAT AS "THOSE FOUR NO LONGER REACH THE NURSE'S BROWSER",
 * which is what an earlier version of this comment implied. Measured by
 * security-review: `equipment`, `nextOfKinPhone` and `notes` are gone from
 * every nurse-reachable response — but **`room` still ships**, via
 * `GET /api/meta`'s own-home resident roster (`{id, displayName, room}`) and
 * the `POST /api/residents` 201. That is legitimate — the request form
 * prefills the room from the roster — but it is a different claim, and a later
 * author building on the stronger one would be building on something false.
 * The accurate statement is: this endpoint no longer carries them.
 */
import type {
  AskKind,
  AskState,
  DeclineReason,
  RequestStatus,
  ReturnAtState,
  SchedulePattern,
  TimeMeaning,
} from '../shared/request-schema.js';
import type { PortalRequestListItem, RequestHistoryEntry, RequestHistoryKind } from './requests-repo.js';
import type { PortalRole } from '../shared/roles.js';

/**
 * ⛔ DECLARED INDEPENDENTLY, NOT DERIVED — and the derivation in this codebase
 * runs the OPPOSITE way to intuition, which is the trap.
 * `OpsRequestListItem = RequestListItem & {…}` (`requests-repo.ts:247`), i.e.
 * **the operator type derives from the nurse type**. So the obvious edit —
 * narrowing `RequestListItem` to the keep-set — would silently strip fields
 * off *Helen's* screen too. The two shapes are declared apart so neither can
 * move the other.
 */
export type NurseRequestListItem = {
  requestId: number;
  /**
   * ⭐ T1683 M3 (A1) - NURSE KEEP-SET WIDENING 1 OF 2 (M7 adds the second).
   *
   * ⛔ THIS IS NOT THE WIDENING THAT WAS REJECTED. The rejected one carried
   * `homeId` - "a per-request Article 9-adjacent record joinable to a durable
   * billing identity". A job reference identifies no person: it names a JOURNEY.
   * ⚠️ RE-TAKEN AT BOARD 2707 M6, AND THE ARGUMENT NARROWED WITH IT. This said an
   * OPAQUE reference "is joinable to NOTHING outside this system", which is untrue
   * of `LCC 05`: the account form carries its home's code and a job count, the
   * same string the home's monthly invoice line carries. What it discloses is the
   * account's own count to that account's own nurse, who already reads the home's
   * name on every card; it is still no billing identity of a PERSON. And its
   * purpose-necessity IS the requirement - A1 exists so one job has one string
   * both sides of a phone call can read aloud, which a payload that omits it
   * cannot deliver. Minimisation asks whether a field is needed for the stated
   * purpose, and this field IS the stated purpose.
   *
   * ⛔ NULL FOR EVERY REQUEST WRITTEN BEFORE M3, permanently - there is no
   * backfill. The renderer omits the line entirely rather than printing a blank
   * label.
   */
  jobRef: string | null;
  residentDisplayName: string;
  destinationName: string;
  /**
   * ⭐ T1683 M7 (A4) — NURSE KEEP-SET WIDENING 2 OF 2, and the pair is now
   * closed: `jobRef` was 1.
   *
   * ⛔ IT IS HER OWN ANSWER READ BACK, which is why it passes minimisation
   * without argument. It is a fact about a JOURNEY — one boolean, no resident
   * content, joinable to nothing — and the nurse is the person who stated it.
   * A payload that drops it leaves the one screen she can check her request on
   * unable to show the thing most likely to have been mis-tapped.
   *
   * ⛔ AND IT IS RENDERED, not merely carried: `MyRequests.tsx` prints it beside
   * the destination on a return job and omits the line entirely on a one-way
   * one. This keep-set's own contract is "the fields the row actually renders";
   * a key added here without a render would make that sentence false and would
   * be a widening for nothing.
   *
   * ⭐ A TIME ACCOMPANIES IT FROM T1717 M5, REVERSING THIS PARAGRAPH'S RULING.
   * Alex, 2026-09-02, via `/scope` of the QA report (question 708 in the lane
   * store), reversed board #1681's deferral; boards #1681 and #1683 M7 record
   * it. `returnAt` is the field below, and it landed WITH its render.
   */
  returnTrip: boolean;
  /**
   * ⭐⭐ T1717 M5 (DD4, P0-7) — NURSE KEEP-SET WIDENING 3, AND IT PASSES
   * MINIMISATION ON THE SAME GROUND `returnTrip` DOES: it is a fact about a
   * JOURNEY — one instant, no resident content, joinable to nothing — and it is
   * the nurse's OWN answer read back on the one screen she can check her request
   * on. A payload that drops it leaves her unable to check the half of the
   * booking most easily mistyped.
   *
   * ⛔ AND IT IS RENDERED, not merely carried — `MyRequests.tsx` prints it inside
   * the destination cell after "and back". This keep-set's contract is "the
   * fields the row actually renders"; a key added here without a render would
   * make that sentence false and would be a widening for nothing.
   *
   * ⛔ NULL ON EVERY ONE-WAY JOB, EVERY RECURRING JOB AND EVERY PRE-v11 REQUEST.
   * The render is gated on `returnTrip` FIRST, so a null on a return job (a
   * recurring one) prints the phrase without an instant rather than the word
   * "null" or an invented time.
   * ⚠️ AND, FROM T2088 M2, ON EVERY RETURN WHOSE TIME IS STILL TO BE CONFIRMED:
   * the null is no longer one population. `returnAtState` below says which.
   */
  returnAt: string | null;
  /**
   * ⭐⭐ T2088 M2 (ADR-0006) — NURSE KEEP-SET WIDENING 7, AND IT PASSES
   * MINIMISATION ON THE SAME GROUND `returnAt` DOES: one of two fixed words
   * about a JOURNEY, no resident content, joinable to nothing, and it is the
   * nurse's OWN answer read back on the one screen she can check her request
   * on. Without it a `'to-confirm'` row renders on her card exactly as a timed
   * return with the time missing, and she cannot tell "I said it is to be
   * confirmed" from "the time I typed was lost".
   *
   * ⛔ IT IS THE DISCRIMINATOR FOR THE NULL ABOVE, so it travels whenever
   * `returnAt` does; a render that reads the instant and not this word mixes
   * two populations. ⚠️ CARRIED FROM M2, RENDERED BY A LATER MILESTONE OF THE
   * SAME PLAN (`docs/plans/ecmb-portal-pilot-bundle-plan.md`): the projection
   * widens in the milestone that makes the row LEGAL, so the wire and the row
   * agree from the first `'to-confirm'` write, and the card's render lands
   * with the form that sends the marker. Until then the key arrives as
   * `'unset'` on every row, which is what every row genuinely is.
   */
  returnAtState: ReturnAtState;
  journeyDate: string | null;
  schedulePattern: SchedulePattern | null;
  timeMeaning: TimeMeaning | null;
  status: RequestStatus;
  /**
   * ⭐⭐ T1749 M7 (R2) — NURSE KEEP-SET WIDENING 4, AND IT IS THE ONE
   * T1868 M5 MEASURED OUT AND NAMED THE TRIGGER FOR. That entry said: "THE
   * TRIGGER THAT WOULD REVISIT IT: board #1749's design pass on this row, which
   * is where a fifth line can be paid for." This is that pass. The four-track
   * grid became `Card`'s STACK, so the row no longer has a line budget to
   * exceed; the 240px ceiling was re-measured on the stack rather than bumped.
   *
   * ⛔ MINIMISATION LINE, unchanged in kind from `returnAt`'s: it is a fact
   * about a JOURNEY — one instant, no resident content, joinable to nothing —
   * and it answers the one question the nurse opens this screen to ask, "when
   * will they come". `Pickup` is the fact she scans for and the sitting ruled it
   * the BIG row of the card's facts table.
   *
   * ⛔ NULL IS "NO PICKUP STATED" AND IS PERMANENT on every pre-v12 row and on
   * every `collection` run (where the stated time IS the pickup and lands
   * through `timeMeaning` instead). Every render OMITS the row rather than
   * printing a blank label.
   */
  pickupAt: string | null;
  /**
   * ⭐ T1749 M7 (R2) — WHO ASKED, AS TWO WORDS, DERIVED ON THE SERVER.
   *
   * ⛔ IT IS NOT A NAME AND MUST NEVER BECOME ONE. `nurseDisplayName` is a
   * CANARY on this file's spec: naming the colleague would put one identified
   * member of staff's activity on another's screen, which is a disclosure
   * nobody has a ground for. The comparison happens HERE, against the session's
   * own `nurseId`, and only its ANSWER crosses — a client-side comparison would
   * need the id and the name on the wire, which is the thing being avoided.
   *
   * ⚠️ Q136 is the open row that asks whether the two words are the right
   * words; it is a COPY question over a shape that is already minimised, and the
   * shape does not wait on it.
   */
  askedForBy: 'you' | 'a colleague';
  /**
   * ⭐⭐ T2075 M2 (plan D8, A2) — NURSE KEEP-SET WIDENING 6: WHO ASKED FOR THE
   * JOURNEY, as the STAFF member's display name off `portal.staff`, or null.
   *
   * ⛔ IT IS THE STAFF FACT, NEVER THE ACCOUNT. `nurseDisplayName` — the sign-in
   * account that TYPED the request — stays the canary on this file's spec and
   * still never crosses; `askedForBy`'s two words are untouched. A staff member
   * is a name on the home's own roster that the home's own nurse picked from,
   * so "Asked for by Máire Ní Bhriain" on a colleague's card is the roster read
   * back, not one account's activity shown to another (A2, Alex: "so a
   * colleague can follow it up"). Recorded as a ruling reversal in
   * OPEN-QUESTIONS by T2075 M3 Step 7, which is also where the card renders it.
   *
   * ⛔ NULL IS PERMANENT for every row written before v13, for every request
   * sent without a pick in M2, and for a staff row since erased (`ON DELETE SET
   * NULL`); the render OMITS the slot rather than printing a label over a blank.
   * ⛔ KEYED ON THE ID, NOT THE NAME: `requestedBy` is the joined name ONLY when
   * `requestedByStaffId` is non-null. The LEFT JOIN can never yield a name
   * without its id, so this is defence in depth — and it is what the spec's
   * second canary (`CANARY-STAFF-…` on a row with no `requested_by`) grades.
   */
  requestedBy: string | null;
  /**
   * ⭐⭐ board 2707 (M1, R-1) — NURSE KEEP-SET WIDENING 8: WHEN THE REQUEST WAS
   * MADE, as an ISO instant, or null.
   *
   * ⛔ MINIMISATION LINE: a fact about the REQUEST, not about the resident — one
   * instant, no resident content, joinable to nothing outside this system — and
   * the card renders it (`MyRequests.tsx`, the `Requested` row), so it is never
   * a key carried for no reader. `updatedAt` stays off: nothing renders it.
   * ⚠️ ON A COLLEAGUE'S ROW IT IS A WEAK AUTHORSHIP SIGNAL, AND THAT IS ACCEPTED:
   * `askedForBy` deliberately says only "a colleague" there, yet `createdAt` can
   * be joined to a shift rota to infer who typed the request. Accepted because
   * the card itself shows the minute in its `Requested` row, which is all a rota
   * join needs, so the wire's finer precision adds nothing a reader of the card
   * lacks (board 2707); whether the
   * home should see it at all is Alex's question, raised in
   * `docs/OPEN-QUESTIONS.md` at the plan's M7. Not truncated here.
   * ⛔ NULL ONLY FOR A ROW WHOSE VALUE IS NOT A STRING: the column is NOT NULL,
   * so this is the projection refusing to throw on a list row, never a state
   * the database produces. The render omits the row on null.
   */
  createdAt: string | null;
  /**
   * ⭐⭐ board 2732 (M1, R-10) — NURSE KEEP-SET WIDENING 9, NARROWED: WHETHER
   * EAST COAST MINIBUS HAS SEEN THE REQUEST, never WHEN. True exactly when the
   * row's `seen_at` is a parseable instant (`isParseableInstant`); false when it
   * was never seen, was seen before v16 added the column, or holds a non-string.
   *
   * ⛔ THE TIME WAS REMOVED BY BOARD 2732 (Alex, 2026-10-02), so the portal does
   * not read as monitoring the office: board 2707 shipped the minute, and a run
   * of minutes told a home when ECMB works. The card's step line and its waiting
   * sentence read this bit (`MyRequests.tsx` `seenFor`). The ops list the kiosk
   * reads keeps the full instant; only the home-facing wires narrowed.
   */
  seen: boolean;
  /**
   * ⛔⛔ T1749 M7 (R2) — PRESENT ON THE VIEWER'S OWN ROW, AND (board 2732 M2,
   * ADR-0008) ON EVERY ROW FOR A `reception` / `admin` VIEWER AT A HOME WHOSE
   * CROSS-GROUP SWITCH IS ON; ABSENT (NOT EMPTY) OTHERWISE. The key is optional
   * for exactly that reason: an explicit `''` would be indistinguishable, to a
   * reader of the wire, from a colleague's note that happened to be blank, and
   * the key-set arm could not tell the two apart either. Absence is the assertion.
   *
   * ⛔ THE NOTE IS FREE TEXT A NURSE TYPED ABOUT A RESIDENT, so it is the one
   * field on this projection whose audience is narrower than the home. Her own
   * words read back to her is her own data; on a colleague's screen they are a
   * disclosure. The widening to reception and admin is Alex's ruling (board 2732
   * note #4743), recorded in ADR-0008, and rides the switch that is already the
   * home's consent to a wider read: a `group` account never gains a colleague's
   * note, and with the switch OFF nothing changes. The spec's canary carries a
   * colleague's note and asserts it never serialises outside that one cell.
   */
  notes?: string;
} & RequestChangeView;

/**
 * ⭐⭐ Board 2788 M3 (plan step 4) - THE CHANGE, THE ASK AND THE HISTORY, ON BOTH
 * HOME-FACING WIRES (this keep-set and `pickHomeRequest`'s), declared once.
 * ⚠️ CARRIED AT M3, RENDERED AT M5 OF THE SAME PLAN (the `returnAtState`
 * precedent above): the server half lands with the routes that write these
 * facts, and the card that reads them lands with its own milestone.
 * ⛔ MINIMISATION LINE, each a fact about the JOURNEY or the home's own ask:
 *   homeChanged      - the home changed this New or Priced request and East
 *                      Coast Minibus has not acted on it since. ⛔ ANDed with the
 *                      status here (DD-14): a mark never reads on an ended or
 *                      Confirmed request, whatever the column says.
 *   askKind/askState - the one ask slot (`ASK_KINDS`, `ASK_STATES`).
 *   askJourneyDate, askPickupAt - what the home asked for, as the ask stored it.
 *   askDeclineReason - Helen's closed-list reason code (`DECLINE_REASONS`), never
 *                      free text (S8); the card words it.
 * ⛔ NOT `revision`, `askSeq` or `askReturnAt`: the kiosk's concurrency tokens and
 * a value no home card renders stay on the ops list.
 *   history          - newest first, capped by the repo, read through the SAME
 *                      scope predicate as the list (S19). Each line names its
 *                      actor as two or three WORDS (`wordRequestHistory`), never
 *                      a nurse id or name; an erased nurse reads "a colleague".
 */
export type RequestChangeView = {
  homeChanged: boolean;
  askKind: AskKind | null;
  askState: AskState;
  askJourneyDate: string | null;
  askPickupAt: string | null;
  askDeclineReason: DeclineReason | null;
  history: RequestHistoryLine[];
};

/** The keys `RequestChangeView` adds to BOTH home-facing keep-sets, in order. */
export const REQUEST_CHANGE_VIEW_KEYS = [
  'homeChanged',
  'askKind',
  'askState',
  'askJourneyDate',
  'askPickupAt',
  'askDeclineReason',
  'history',
] as const;

/** Who acted, as the home reads it: never a name, never an id (S19, S21). */
export type HistoryActorWords = 'you' | 'a colleague' | 'East Coast Minibus';

/** One history line on a home-facing wire. `fromAt`/`toAt` are the request's own
 *  journey instants (DD-11), null on the kinds that carry none. */
export type RequestHistoryLine = {
  kind: RequestHistoryKind;
  fromAt: string | null;
  toAt: string | null;
  at: string;
  by: HistoryActorWords;
};

/** The keys of one line, pinned by name in the spec. */
export const REQUEST_HISTORY_LINE_KEYS = ['kind', 'fromAt', 'toAt', 'at', 'by'] as const;

/**
 * ⭐ Board 2788 M3 (S19): WHO ACTED, WORDED ON THE SERVER against the session's
 * own `nurseId`, for `askedForBy`'s reason (a browser-side comparison would need
 * the id on the wire). The ops door is "East Coast Minibus"; the viewer's own
 * account is "you"; every other account is "a colleague", and that INCLUDES an
 * erased nurse, whose `actor_nurse_id` (no FK, retained by the erasure runbook)
 * no longer resolves to anybody and can never equal a live session's id.
 */
export function wordRequestHistory(
  entries: readonly RequestHistoryEntry[],
  viewerNurseId: number,
): RequestHistoryLine[] {
  return entries.map((e) => ({
    kind: e.kind,
    fromAt: e.fromAt,
    toAt: e.toAt,
    at: e.at,
    by: e.actorNurseId === 'ops' ? 'East Coast Minibus' : e.actorNurseId === viewerNurseId ? 'you' : 'a colleague',
  }));
}

/** The list row a home-facing pick reads: the portal's own list item plus its
 *  history, already worded for the viewer by the route. */
export type HomeFacingHistory = { history: readonly RequestHistoryLine[] };

/**
 * The change view of one row, PICKED key by key, for both home-facing wires.
 * ⛔ A non-array history is no history and a line is rebuilt by name, never
 * spread: a list row never throws, and nothing a line gained later crosses.
 */
export function pickRequestChangeView(row: PortalRequestListItem & HomeFacingHistory): RequestChangeView {
  return {
    homeChanged: row.homeChanged === true && (row.status === 'new' || row.status === 'priced'),
    askKind: row.askKind,
    askState: row.askState,
    askJourneyDate: row.askJourneyDate,
    askPickupAt: row.askPickupAt,
    askDeclineReason: row.askDeclineReason,
    history: Array.isArray(row.history)
      ? row.history.map((h) => ({ kind: h.kind, fromAt: h.fromAt, toAt: h.toAt, at: h.at, by: h.by }))
      : [],
  };
}

/**
 * ⛔⛔ THE COUNT IS GONE FROM THIS SENTENCE, AND ITS OWN PARENTHESIS IS WHY.
 * It said "TEN KEYS — nine until T1717 M5 added `returnAt`" while warning, in
 * the same breath, that "the count words in this package have gone stale three
 * times"; T1868 M5 added `pickupAt` and made it eleven, which would have been
 * the fourth. Derive it from the list below. WHAT MATTERS AND DOES NOT ROT:
 * SEVERAL OF THESE KEYS ARE LOAD-BEARING IN A WAY THAT FAILS SILENTLY.
 *
 * `journeyDate`, `schedulePattern` and `timeMeaning` all feed ONE renderer, and
 * ⭐ AT T1749 M4 THAT RENDERER MOVED — the necessity argument is re-pointed, not
 * re-opened, and the keep-set is unchanged. It was `formatWhenWithMeaning(item)`
 * in `portal/src/client/format.ts`; that function, `formatWhen`, the host-locale
 * `formatInstant` and the client-side `formatTimeMeaning` are all DELETED. Both
 * surfaces now render through `@ecmb/ui`'s `format-when.ts`: the nurse's card via
 * `formatWhenParts` (`MyRequests.tsx`'s `whenFor`), Helen's queue via
 * `formatWhenTwoLines` → `formatWhenLine` (`Ops.tsx`'s `whenCell`). Both are
 * pinned to `en-IE` / `Europe/Dublin`. The three keys are as load-bearing as they
 * were and fail in the same silent way:
 *   · drop `journeyDate` → `formatWhenParts` falls through both branches on a
 *     one-off request and returns its no-date sentence. **The day on every
 *     one-off row becomes `Date not given`.**
 *   · drop `timeMeaning` → every stated time loses its meaning word: the nurse
 *     card puts it in the neutral `Time` row rather than `Appointment` or
 *     `Pickup`, and Helen's queue reads `Time 10:00` on every row. The
 *     appointment/collection distinction is gone from both screens.
 *   · ⭐ THE FOURTH (T1717 M5): drop `returnAt` — or emit the key with a null —
 *     and `formatReturnAt` returns `''`, so the destination cell reads "…and
 *     back" with no time on a job that has one. The row still looks complete,
 *     which is what makes it the same silent class as the two above rather than
 *     a visible break.
 * ⚠️ IN BOTH CASES THE KEY-SET ASSERTION STAYS GREEN, because the payload still
 * equals the keep-set it was told to expect.
 * ⛔ SO THIS FILE'S SPEC PINS THE VALUES, not just the keys — that is what
 * catches a dropped field here.
 *
 * ⭐ AND THE THIRD LAYER NOW EXISTS: `portal/test/client/my-requests-when.spec.tsx`
 * drives the nurse card through a REAL RENDER and reads the cells she looks at,
 * so a defect INSIDE the formatter — as opposed to a field missing from this
 * payload — is guarded too. ⛔ THIS PARAGRAPH USED TO SAY THAT PIN DID NOT EXIST
 * AND WAS "outstanding for part 2"; part 2 built it, and leaving the old wording
 * would have been the same over-claim class in reverse — the next reader either
 * rebuilds the pin or retires it as unbacked.
 * ⚠️ THE CAVEAT HERE CHANGED AT T1749 M4 AND IS WORTH THE LINE. That spec used to
 * be unable to assert any formatted date string, because the old renderer called
 * `toLocaleString(undefined, …)` and an exact expectation reds on any machine in
 * another locale or zone. The shared formatter is PINNED (`en-IE`,
 * `Europe/Dublin`), so the exact clock IS now assertable and the spec asserts it
 * on the operator cell. The card's own arms stay locale-invariant on purpose —
 * they grade WHICH labelled row a time lands in, which is the defect class this
 * keep-set can cause.
 *
 * `payerKind` is declared in `MyRequests.tsx`'s local type and genuinely unused,
 * so it is dropped.
 */
export const NURSE_REQUEST_KEEP_KEYS = [
  'requestId',
  'jobRef',
  'residentDisplayName',
  'destinationName',
  // ⭐ T1683 M7 (A4) - NURSE KEEP-SET WIDENING 2. Her own answer, read back: a
  // boolean about a JOURNEY, no resident content, and rendered beside the
  // destination on the row rather than merely carried.
  'returnTrip',
  // ⭐ T1717 M5 (DD4) - WIDENING 3, and the last this plan makes. The INSTANT
  // beside the boolean, on the same minimisation ground and rendered in the same
  // cell. ⛔ It is dropped from Helen's side of nothing: the OPS list keeps
  // carrying the full row, and this list is the nurse's alone.
  'returnAt',
  // ⭐⭐ T2088 M2 (ADR-0006) — WIDENING 7: the WORD that says what the null
  // beside it means. One of two fixed values about a journey, no resident
  // content, her own answer read back; the minimisation line is `returnAt`'s
  // and the same reader renders both. See the type above.
  'returnAtState',
  // ⭐⭐ T1749 M7 (R2) — WIDENING 4, AND IT IS T1868 M5's MEASUREMENT REVERSED
  // BY THE TRIGGER THAT MEASUREMENT ITSELF NAMED.
  //
  // T1868 M5 ADDED THIS KEY AND THEN MEASURED IT OUT AGAIN, and the record is
  // kept rather than erased. The argument FOR it was the one `returnAt` above
  // won on: a fact about a JOURNEY, no resident content, the nurse's own answer
  // read back. What defeated it was this keep-set's OTHER contract, "the fields
  // the row actually renders": measured on the live seat 2026-09-08, the extra
  // phrase took `.my-requests-row` from 229px to 259px at every width from 768
  // to 1024, against a 240px cap in `e2e/tests/portal/portal-narrow-layout.spec.ts`
  // with 11px of headroom. That entry ended: "THE TRIGGER THAT WOULD REVISIT IT:
  // board #1749's design pass on this row, which is where a fifth line can be
  // paid for."
  // ⛔ THIS IS THAT PASS, AND THE LAYOUT THE 240 WAS MEASURED ON NO LONGER
  // EXISTS. The four-track grid became `Card`'s STACK at T1723 M7, the cap was
  // re-cut with it, and M7 re-measured it again with this row on the card. ⇒ THE
  // CEILING IS DERIVED, NEVER BUMPED: the live figures and the widths they were
  // read at are in `portal-narrow-layout.spec.ts` beside the caps, which is the
  // one place that can measure a rendered box. Do not quote a number here.
  // ⛔ AND IT LANDED WITH ITS RENDER, as every widening in this list has: the
  // facts table's BIG `Pickup` row (`MyRequests.tsx`'s `whenFor`), which is the
  // sitting's own ruling about what the nurse scans for.
  'pickupAt',
  // ⭐ T1749 M7 (R2) — WIDENING 5: two words, never a name. Derived on the
  // server from `row.nurseId === session.nurseId`, so the id and the display
  // name both stay off the wire. See the type above for why the comparison
  // cannot move to the client.
  'askedForBy',
  // ⭐⭐ T2075 M2 (plan D8) — WIDENING 6: the STAFF member's name, or null. See
  // the type above for why a staff name is not the disclosure the two words
  // avoid. `groupLabelAtBooking` and `requestedByStaffId` are on the row and
  // are NOT here: the group is M4's home-wide keep-set's, the id is a join key.
  'requestedBy',
  // ⭐⭐ board 2707 (M1, R-1) — WIDENING 8: when the request was made. A fact
  // about the REQUEST, no resident content, joinable to nothing, and rendered
  // as the card's `Requested` row in the same change. See the type above.
  'createdAt',
  // ⭐⭐ board 2707 (M4, R-5) — WIDENING 9, narrowed by board 2732 (Alex,
  // 2026-10-02) from the minute to a yes/no: WHETHER the office has seen it,
  // never when. Written only by the ops door, so it names no nurse. See the type.
  'seen',
  'journeyDate',
  'schedulePattern',
  'timeMeaning',
  'status',
  // ⭐⭐ Board 2788 M3 - WIDENING 10: the home's change mark, its ask and the
  // request's history. See `RequestChangeView` for each key's minimisation line
  // and for why they are carried a milestone before the card renders them.
  ...REQUEST_CHANGE_VIEW_KEYS,
] as const;

/**
 * ⛔ THE OWN-ROW KEY SET: the keep-set PLUS `notes`, and it is a second list
 * rather than a flag because the two payloads genuinely have different key sets
 * and every pin in this package compares KEY SETS. A colleague's row must not
 * carry the key at all (see the type), so "the keep-set" alone is the wrong
 * expectation for half the rows and the right one for the other half. Since
 * board 2732 M2 it is also EVERY row's key set for a reception / admin viewer
 * behind the switch (ADR-0008).
 * ⚠️ `notes` IS NULLABLE, AND THE FAIL-CLOSED NOTE FOR IT LIVES AT THE WRITE
 * SIDE: `portal/src/core/requests-repo.ts` (the INSERT's `next_of_kin_name` /
 * `notes` block) records why a nullable column is handled the way it is and how
 * the hazard inverts if it is ever made NOT NULL. A projection reader who wants
 * that reasoning follows the pointer there rather than re-deriving it here.
 */
export const NURSE_REQUEST_OWN_ROW_KEYS = [
  ...NURSE_REQUEST_KEEP_KEYS,
  'notes',
] as const;

/**
 * The six-stage spine, ruled as **Option B — full spine, honest gaps**.
 * ⛔ Order is the journey's order and is not configurable.
 *
 * ⭐⭐ T1749 M7 — THIS ARRAY STAYS EXPORTED WHILE ITS ONLY FUNCTION LEAVES, AND
 * THAT IS DELIBERATE RATHER THAN RESIDUE. `nurseLifecycle()` is deleted with the
 * `lifecycle` field it fed; this constant has a reader that is not an import.
 * `quote-helper/test/client/lifecycle-stage-parity.spec.ts` READS THIS FILE'S
 * SOURCE TEXT and matches the declaration below by regex, because the two
 * packages cannot import each other's `core/`. Delete or rename it and that
 * parity pin reds with "the pin is reading the wrong thing".
 * ⚠️⚠️ AND DO NOT WRITE THE DECLARATION'S OWN TEXT INTO A COMMENT HERE. The
 * pin's pattern is NON-GREEDY, so an earlier occurrence anywhere in this file
 * wins: a first draft of this very paragraph quoted the exported line verbatim
 * and the parity spec matched the COMMENT, read zero stages out of it and went
 * red on an untouched constant. Describe the declaration; never spell it.
 * ⇒ it is the declared half of the six-stage, two-tool parity contract, and the
 * kiosk's `KIOSK_LIFECYCLE_STAGES` is the other half.
 */
export const LIFECYCLE_STAGES = [
  { id: 'sent', label: 'Sent' },
  { id: 'quoted', label: 'Quoted' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'travelled', label: 'Travelled' },
  { id: 'invoiced', label: 'Invoiced' },
  { id: 'paid', label: 'Paid' },
] as const;

/**
 * ⛔⛔ `nurseLifecycle()`, `REACHED` AND `NURSE_OPAQUE` ARE DELETED (T1749 M7),
 * AND THE DELETION IS THE POINT RATHER THAN A TIDY-UP.
 *
 * The six-stage spine they computed stopped being rendered when the sitting
 * (2026-09-10) collapsed the nurse's view to Words 3's THREE nodes: `Card`'s
 * request shape derives its step line from the one `status` value, so the word
 * and the lit node cannot disagree. The field went on being projected anyway,
 * which left a server-computed value on an Article 9 wire that no screen read,
 * and a spec grading a function no screen called. Both are the same defect class
 * and M1's security review named it: a projection carries what is RENDERED, and
 * a pin grades what SHIPS.
 *
 * ⇒ the invariants those arms held (exactly one `current` on `new`, `priced` and
 * `confirmed`; NO lit node on `cancelled` / `declined`; the tail nodes never
 * reading as a negative) are RE-POINTED, not dropped. They are graded on the
 * RENDERED card in `portal/test/client/request-card.spec.tsx` — find them by
 * their words: "exactly one step is aria-current for a live request", "an ended
 * request lights NO step", and the arm asserting a declined or cancelled request
 * says its later nodes "will not happen".
 */

/**
 * ⭐ board 2732 (M1, R-10) — THE SEEN BIT AS THE HOME-FACING WIRES CARRY IT:
 * true exactly for a string that parses as an instant, false for anything else.
 * Shared by this pick and `pickHomeRequest`, the two home-facing readers; the ops
 * list (the kiosk's) is projected through neither and keeps the full instant. A
 * list row never throws.
 */
export function isParseableInstant(value: unknown): boolean {
  return typeof value === 'string' && !Number.isNaN(new Date(value).getTime());
}

/**
 * The nurse's view of one request. ⛔ Every key is NAMED; nothing is spread.
 *
 * ⚠️ Takes the full row and returns the narrow shape — the narrowing is the
 * point, so it must not be given an already-narrowed input "for efficiency".
 */
/**
 * WHO IS LOOKING, as the server decides it: the session's account, its live
 * role, and its home's cross-group switch (board 2732 M2, DD2).
 */
export type NurseRequestViewer = {
  nurseId: number;
  role: PortalRole;
  crossGroupEnabled: boolean;
};

/**
 * ⛔⛔ `viewer` IS A REQUIRED SECOND ARGUMENT, AND REQUIRED IS THE SAFETY
 * PROPERTY (T1749 M7; widened from a bare `sessionNurseId` at board 2732 M2).
 * Two of this projection's outputs depend on WHO IS LOOKING — `askedForBy`, and
 * whether `notes` is present at all — so a caller that forgot to say would need
 * a default, and every default is wrong in one direction: "the session nurse"
 * would put a colleague's free text on her screen, "nobody" would quietly hide a
 * nurse's own notes from herself. `tsc` refusing the old call is the mechanism.
 *
 * ⛔ THE DECISION IS HERE AND NOWHERE ELSE, for this file's standing reason: a
 * client-side comparison would need `nurseId` AND `nurseDisplayName` on the
 * wire, which is the disclosure the two words exist to avoid. `notes` crosses
 * when the row is the viewer's own, OR the viewer is `reception` / `admin` and
 * the switch is ON (ADR-0008); `equipment` and the account name never do.
 */
export function pickNurseRequest(
  row: PortalRequestListItem & HomeFacingHistory,
  viewer: NurseRequestViewer,
): NurseRequestListItem {
  const mine = row.nurseId === viewer.nurseId;
  const homeWideNotes = (viewer.role === 'reception' || viewer.role === 'admin') && viewer.crossGroupEnabled === true;
  const picked: NurseRequestListItem = {
    requestId: row.requestId,
    jobRef: row.jobRef,
    residentDisplayName: row.residentDisplayName,
    destinationName: row.destinationName,
    returnTrip: row.returnTrip,
    returnAt: row.returnAt,
    returnAtState: row.returnAtState,
    pickupAt: row.pickupAt,
    askedForBy: mine ? 'you' : 'a colleague',
    requestedBy: row.requestedByStaffId === null ? null : row.requestedByName,
    // ⛔ A non-string becomes null rather than crossing as whatever it is: a
    // list row never throws, and the card omits the row on null.
    createdAt: typeof row.createdAt === 'string' ? row.createdAt : null,
    // ⛔ Whether, never when (board 2732): a non-string or unparseable value is
    // false, and the step line still says what the status implies.
    seen: isParseableInstant(row.seenAt),
    journeyDate: row.journeyDate,
    schedulePattern: row.schedulePattern,
    timeMeaning: row.timeMeaning,
    status: row.status,
    // ⭐ Board 2788 M3: the change, the ask and the history, picked by name.
    ...pickRequestChangeView(row),
  };
  // ⛔ ADDED ONLY ON THE AUTHOR'S OWN ROW, or for reception / admin behind the
  // switch (ADR-0008), so the WIRE carries the answer and no reader has to trust
  // a blank string to mean "withheld".
  if (mine || homeWideNotes) picked.notes = row.notes;
  return picked;
}
